import React, { useRef } from 'react';
import { FiChevronLeft, FiChevronRight, FiUser } from 'react-icons/fi';
import { format, isToday } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useDate } from '../../context/DateContext';
import { useAuth } from '../../context/AuthContext';

interface HeaderProps {
  title?: string;
  showDatePicker?: boolean;
  showProfile?: boolean;
  userName?: string;
  onProfileClick?: () => void;
  rightContent?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  showDatePicker = false,
  showProfile = false,
  userName,
  onProfileClick,
  rightContent,
}) => {
  const { selectedDate, setSelectedDate, goToPrevDay, goToNextDay, goToToday } = useDate();
  const { user } = useAuth();
  const dateInputRef = useRef<HTMLInputElement>(null);

  // 레벨 1, 2만 날짜 변경 가능
  const canChangeDate = user?.level === 1 || user?.level === 2;

  const handlePrevDay = () => {
    if (canChangeDate) {
      goToPrevDay();
    }
  };

  const handleNextDay = () => {
    if (canChangeDate) {
      goToNextDay();
    }
  };

  const handleDateClick = () => {
    if (canChangeDate && dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setSelectedDate(new Date(e.target.value + 'T00:00:00'));
    }
  };

  const handleTodayClick = () => {
    if (canChangeDate) {
      goToToday();
    }
  };

  return (
    <header className="app-header">
      {/* 왼쪽 영역 - 오늘 버튼 */}
      <div className="header-left">
        {showDatePicker && canChangeDate && !isToday(selectedDate) && (
          <button className="today-btn" onClick={handleTodayClick}>
            오늘
          </button>
        )}
      </div>

      {/* 가운데 영역 - 날짜 또는 타이틀 */}
      {showDatePicker ? (
        <div className="date-picker-container">
          <div className="date-picker">
            {canChangeDate && (
              <button className="date-nav-btn" onClick={handlePrevDay}>
                <FiChevronLeft size={20} />
              </button>
            )}
            <div
              className={`date-display ${canChangeDate ? 'clickable' : ''}`}
              onClick={handleDateClick}
            >
              {format(selectedDate, 'M월 d일 (EEE)', { locale: ko })}
              {canChangeDate && (
                <input
                  ref={dateInputRef}
                  type="date"
                  className="hidden-date-input"
                  value={format(selectedDate, 'yyyy-MM-dd')}
                  onChange={handleDateChange}
                />
              )}
            </div>
            {canChangeDate && (
              <button className="date-nav-btn" onClick={handleNextDay}>
                <FiChevronRight size={20} />
              </button>
            )}
          </div>
        </div>
      ) : (
        <h1 className="header-title">{title}</h1>
      )}

      <div className="header-actions">
        {rightContent}
        {showProfile && (
          <button
            className="btn btn-link text-white p-0 d-flex align-items-center gap-2"
            onClick={onProfileClick}
            style={{ textDecoration: 'none' }}
          >
            <span className="d-none d-sm-inline">{userName}</span>
            <FiUser size={24} />
          </button>
        )}
      </div>
    </header>
  );
};
